import React, { useState, useEffect } from "react";
import ReactTable from "react-table";
import { Button, UncontrolledTooltip } from "reactstrap";
import s from "../../styles.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckDouble,
  faClock,
  faFileExport,
  faMeh,
  faTimesCircle,
  faUserSlash
} from "@fortawesome/free-solid-svg-icons";
import permissionCodes from "../../../../../utils/permissionCodes";
import { userHasPermission } from "../../../../../utils/accessTree";
import DeportivaEventoAsignado from "../../../../../types/DeportivaEventoAsignado";
import { formatIsoDate } from "../../../../helpers/formatters";

const {
  COMISIONES_V2_IMPARTICION_EVENTO: IMPARTICION_EVENTO,
  COMISIONES_V2_IMPARTICION_EVENTO_ACTUALIZACION: ACTUALIZACION
} = permissionCodes;

export interface TableProps {
  page: Page<DeportivaEventoAsignado>;
  loading: boolean;
  onFetchData: ({ page, pageSize }) => void;
  onExport: () => void;
  onMarcarImpartido: (row: DeportivaEventoAsignado) => void;
  onMarcarFaltaInstructor: (row: DeportivaEventoAsignado) => void;
  onMarcarFaltaCliente: (row: DeportivaEventoAsignado) => void;
  onActualizar: (row: DeportivaEventoAsignado) => void;
  onCancelar: (row: DeportivaEventoAsignado) => void;
}

export const MemoTable = React.memo(
  ({
    page,
    loading,
    onFetchData,
    onExport,
    onMarcarImpartido,
    onMarcarFaltaInstructor,
    onMarcarFaltaCliente,
    onActualizar,
    onCancelar
  }: TableProps) => {
    // State de las columnas de la tabla
    const [columns, setColumns] = useState(
      columnsFormatter(
        onMarcarImpartido,
        onMarcarFaltaInstructor,
        onMarcarFaltaCliente,
        onActualizar,
        onCancelar
      )
    );

    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    // Actualización de estilos
    useEffect(() => {
      setColumns(
        columnsFormatter(
          onMarcarImpartido,
          onMarcarFaltaInstructor,
          onMarcarFaltaCliente,
          onActualizar,
          onCancelar
        )
      );
    }, []);

    // Actualización de datos de paginación
    useEffect(() => {
      if (page) {
        setPageNumber(page.number);
        setTotalPages(page.totalPages);
      }
    }, [page]);

    const mostrados = page && page.numberOfElements;
    const totales = page && page.totalElements;
    return (
      <>
        {/* Botón para exportar */}
        <div className="row">
          {!!page && !!page.content && !!page.content.length && (
            <>
              <div className="col-6 p-0" style={{ lineHeight: "40px" }}>
                <span style={{ fontWeight: "bold" }}>
                  Eventos mostrados: {mostrados} de {totales}
                </span>
              </div>
              <div
                className="col-6 p-0 text-right"
                style={{ lineHeight: "40px" }}
              >
                <Button
                  hidden
                  color="primary"
                  onClick={onExport}
                  disabled={loading}
                  className="mr-2"
                >
                  <FontAwesomeIcon icon={faFileExport} />
                  {!loading ? " Exportar" : " Espere..."}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Tabla */}
        <div className="row mt-2">
          <div style={{ width: "100%" }}>
            <ReactTable
              manual
              data={(page && page.content) || []}
              columns={columns}
              loading={loading}
              defaultPageSize={100}
              className="-striped -highlight"
              onFetchData={onFetchData}
              page={pageNumber}
              pages={totalPages}
              showPagination={true}
              showPageSizeOptions={false}
              getTrProps={() => ({ style: { height: "30px" } })}
              sortable={false}
            />
          </div>
        </div>
      </>
    );
  }
);

// TODO: Definir permisos para los botones
function columnsFormatter(
  onMarcarImpartido: (row: DeportivaEventoAsignado) => void,
  onMarcarFaltaInstructor: (row: DeportivaEventoAsignado) => void,
  onMarcarFaltaCliente: (row: DeportivaEventoAsignado) => void,
  onActualizar: (row: DeportivaEventoAsignado) => void,
  onCancelar: (row: DeportivaEventoAsignado) => void
) {
  return [
    {
      Header: "Id",
      id: "idEvento",
      width: 88,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: (row: DeportivaEventoAsignado) => {
        const idEvento = row.idClase || row.idParticipacion;
        const tipo = row.idClase ? "CP" : "PD";
        return (
          <div className={`${fieldClassName(row)}`}>
            {`${tipo}-${idEvento}`}
          </div>
        );
      }
    },
    {
      Header: "Club",
      id: "club",
      width: 128,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("club")
    },
    {
      Header: "Factura",
      id: "folioFactura",
      width: 88,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("folioFactura")
    },
    {
      Header: "Estatus",
      id: "estatus",
      width: 88,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: enumAccessor("estatus")
    },
    {
      Header: "Producto",
      id: "producto",
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: simpleAccessor("producto")
    },
    {
      Header: "Instructor",
      id: "instructor",
      className: s.tdClear,
      accessor: simpleAccessor("instructor")
    },
    {
      Header: "Fecha o periodo",
      id: "coordinador",
      width: 120,
      className: s.tdClear,
      headerClassName: "text-left",
      accessor: (row: DeportivaEventoAsignado) => {
        return (
          <div className={`${fieldClassName(row)}`}>
            {row.periodo || formatIsoDate(row.fechaClase) + " " + row.horaClase}
          </div>
        );
      }
    },
    {
      Header: "Cliente",
      id: "persona",
      className: s.tdClear,
      accessor: simpleAccessor("persona")
    },
    {
      Header: "",
      id: "acciones",
      className: s.tdClear,
      width: 176,
      accessor: (row: DeportivaEventoAsignado) => {
        if (row.estatus !== "ASIGNADA") {
          return <div className={`${fieldClassName(row)}`}>&nbsp;</div>;
        }
        return (
          <div className={`${fieldClassName(row)}`}>
            &nbsp;
            {renderBotonImparticion(
              row,
              onMarcarImpartido,
              "Confirmar la " +
                (row.idClase
                  ? "impartición de la clase"
                  : "participación en el programa"),
              "Este evento no pertenece a su club",
              "-impartido",
              faCheckDouble,
              "#004d00"
            )}
            {!!row.idClase &&
              renderBotonImparticion(
                row,
                onMarcarFaltaInstructor,
                "Inasistencia del instructor",
                "Este evento no pertenece a su club",
                "-falta-instructor",
                faMeh,
                "#e6b800"
              )}
            {!!row.idClase &&
              renderBotonImparticion(
                row,
                onMarcarFaltaCliente,
                "Inasistencia del cliente",
                "Este evento no pertenece a su club",
                "-falta-cliente",
                faUserSlash,
                "#e6b800"
              )}
            {renderBotonActualizacion(
              row,
              onActualizar,
              "Reprogramar",
              "Este evento no pertenece a su club",
              "-reprogramar",
              faClock,
              "#e6b800"
            )}
            {renderBotonActualizacion(
              row,
              onCancelar,
              "Cancelar",
              "Este evento no pertenece a su club",
              "-cancelar",
              faTimesCircle,
              "#600"
            )}
            &nbsp;
          </div>
        );
      }
    }
  ];
}

function simpleAccessor(key: keyof DeportivaEventoAsignado) {
  return (row: DeportivaEventoAsignado) => {
    return <div className={fieldClassName(row)}>{row[key]}&nbsp;</div>;
  };
}

function enumAccessor(accessor: string) {
  return row => {
    const str = row[accessor] || "";
    const sentence = str.toLowerCase().split("_");

    for (var i = 0; i < sentence.length; i++) {
      if (sentence[i]) {
        sentence[i] = sentence[i][0].toUpperCase() + sentence[i].slice(1) + " ";
      }
    }

    return <div className={fieldClassName(row)}>{sentence}</div>;
  };
}

function fieldClassName(row: DeportivaEventoAsignado) {
  // Si la factura está cancelada, aplicar clase especial
  if (!row.isPagado) {
    return `${s.field} ${s.facturaCancelada}`;
  }

  return row.permisoClub ? `${s.field}` : `${s.field} ${s.noPermitido}`;
}

function renderBotonImparticion(
  row: DeportivaEventoAsignado,
  action,
  message,
  clubError,
  idPostfix,
  icon,
  color
) {
  if (!userHasPermission(IMPARTICION_EVENTO)) {
    return false;
  }

  const idEvento = row.idClase || row.idParticipacion;
  const tipo = row.idClase ? "clase" : "participación";
  const id = `id-${idEvento}-${tipo}-btn-${idPostfix}`;

  if (!row.isPagado) {
    return renderBoton(
      row,
      id,
      false,
      action,
      "La venta ha sido cancelada, no se puede marcar la impartición",
      "#600",
      icon
    );
  } else if (row.permisoClub) {
    return renderBoton(row, id, true, action, message, color, icon);
  } else {
    return renderBoton(row, id, false, action, clubError, "#999", icon);
  }
}

function renderBotonActualizacion(
  row: DeportivaEventoAsignado,
  action,
  message,
  clubError,
  idPostfix,
  icon,
  color
) {
  if (!userHasPermission(ACTUALIZACION)) {
    return false;
  }

  const idEvento = row.idClase || row.idParticipacion;
  const tipo = row.idClase ? "clase" : "participación";
  const id = `id-${idEvento}-${tipo}-btn-${idPostfix}`;

  if (!row.isPagado) {
    return renderBoton(
      row,
      id,
      false,
      action,
      "La venta ha sido cancelada. No se puede actualizar",
      "#600",
      icon
    );
  } else if (row.permisoClub) {
    return renderBoton(row, id, true, action, message, color, icon);
  } else {
    return renderBoton(row, id, false, action, clubError, "#999", icon);
  }
}

function renderBoton(
  row: DeportivaEventoAsignado,
  id,
  habilitado,
  action,
  tooltip,
  color,
  icon
) {
  if (habilitado) {
    return (
      <span id={id} onClick={() => action(row)} className={s.btn}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  } else {
    return (
      <span className={s.info} id={id}>
        <FontAwesomeIcon icon={icon} color={color} />
        <UncontrolledTooltip placement="left" target={id}>
          {tooltip}
        </UncontrolledTooltip>
      </span>
    );
  }
}
